import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PERSONA_ICONS } from "@/components/PersonaBadge";

/**
 * AdvisorRolodex — 10 clickable tiles introducing each investor persona.
 *
 * Two modes:
 *   Preview mode (no completed idea)   → clicking a tile toggles a checklist.
 *   Deep-link mode (completed idea exists)
 *                                       → clicking jumps into that idea's
 *                                         verdict page with the coach drawer
 *                                         auto-opened for that persona.
 */
export default function AdvisorRolodex() {
  const nav = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Most recent completed idea (has panel_results). Powers the deep link.
  const [deepLinkIdeaId, setDeepLinkIdeaId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // /personas is public; /ideas requires auth and 401s cleanly if not signed in.
        const [personasR, ideasR] = await Promise.all([
          api.get("/personas"),
          api.get("/ideas").catch(() => ({ data: { ideas: [] } })),
        ]);
        if (cancelled) return;
        setPersonas(personasR.data.personas || []);
        const completed = (ideasR.data.ideas || []).find(
          (idea) => idea.status === "complete" && idea.panel_results && !idea.panel_results.error,
        );
        if (completed) setDeepLinkIdeaId(completed.id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function onTileClick(personaId) {
    if (deepLinkIdeaId) {
      nav(`/ideas/${deepLinkIdeaId}/verdict?coach=${personaId}`);
    } else {
      setOpenId(openId === personaId ? null : personaId);
    }
  }

  if (loading || !personas.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-8 py-24" data-testid="advisor-rolodex">
      <div className="mb-10">
        <div className="mono-caps text-slate-500 mb-3">The Panel</div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl">
          {deepLinkIdeaId
            ? "Ten investors judged your plan. Ask any of them a follow-up."
            : "Ten investors will judge your plan. Meet them before you submit."}
        </h2>
        <p className="text-slate-600 text-sm mt-4 max-w-2xl leading-relaxed">
          {deepLinkIdeaId
            ? "Each of these advisors already scored your last completed plan. Tap any one to open their coaching thread."
            : "Five angels. Five seed funds. Each with a distinct voice, check size, and three specific things they need to see. Click any to preview what they care about."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {personas.map((p) => {
          const Icon = PERSONA_ICONS[p.icon];
          const isOpen = openId === p.id && !deepLinkIdeaId;
          const ctaLabel = deepLinkIdeaId ? "Ask →" : isOpen ? "− Hide" : "+ Checklist";
          return (
            <button
              key={p.id}
              onClick={() => onTileClick(p.id)}
              className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow relative overflow-hidden group"
              data-testid={`rolodex-tile-${p.id}`}
            >
              <span
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ backgroundColor: p.color }}
                aria-hidden
              />
              <div className="flex items-center gap-2 mb-3">
                {Icon && (
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                    style={{ backgroundColor: `${p.color}15`, color: p.color }}
                  >
                    <Icon size={16} strokeWidth={2.4} />
                  </span>
                )}
                <div className="min-w-0">
                  <div className="mono-caps text-[0.6rem]" style={{ color: p.color }}>
                    {p.id} · {p.stage === "angel" ? "Angel" : "Seed"}
                  </div>
                  <div className="mono-caps text-[0.55rem] text-slate-400 truncate">
                    {p.check_size}
                  </div>
                </div>
              </div>
              <div className="font-display text-base leading-tight mb-2">{p.name}</div>
              <div className="text-[0.7rem] text-slate-500 italic leading-snug line-clamp-3">
                "{p.voice_preview}"
              </div>
              <div className="mt-3 mono-caps text-[0.6rem]" style={{ color: p.color }}>
                {ctaLabel}
              </div>
              {isOpen && (
                <ul
                  className="mt-3 space-y-2 text-[0.72rem] text-slate-700 leading-snug border-t border-slate-100 pt-3"
                  data-testid={`rolodex-checklist-${p.id}`}
                >
                  {(p.cares_about || []).map((c, i) => (
                    <li key={i} className="flex gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0 mt-1"
                        style={{ backgroundColor: p.color }}
                      />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
